// frontend/src/features/onboarding/steps/FirstValueStep.tsx
//
// Wizard step 3 — the one first-value action (Task 18). This is the wizard's
// payoff. It branches on the capability answers collected in step 2:
//
//   - selectedBrands.length > 0  → ConnectDevice variant: the extracted
//     AddDeviceForm with the FIRST selected brand pre-selected. On a successful
//     connect we kick off the existing 30-day refresh WITHOUT blocking (data
//     streams into the dashboard over the next minutes — that trickle is itself
//     the activation moment), then advance to the finish step. Remaining brands
//     are NOT looped (per design §3.1): a second credentials wall is where
//     mixed-persona users churn; they surface later via the checklist.
//
//   - selectedBrands empty (test-kits persona) → FirstTest variant: the embedded
//     WaterTestForm. On a successful log we advance to the finish step; the
//     parameter tile is live the moment they land.
//
// Either branch is skippable ("Skip for now") — a skip just advances to the
// finish step (which is what marks onboarding complete), so the gate never
// traps anyone in /welcome.
import { Anchor, Stack, Text, Title } from '@mantine/core';
import { useTank } from '@/contexts/TankContext';
import { api } from '@/api/client';
import { AddDeviceForm } from '@/features/devices/AddDeviceForm';
import { WaterTestForm } from '@/components/UnifiedManualEntryModal/WaterTestForm';
import { trackOnboarding } from '@/lib/onboardingAnalytics';
import { useOnboardingWizard } from '../WizardContext';

export function FirstValueStep() {
  const { selectedTank } = useTank();
  const { state, setField, next } = useOnboardingWizard();

  const firstBrand = state.selectedBrands[0] ?? null;
  const isDeviceBranch = Boolean(firstBrand);

  const handleDeviceSuccess = () => {
    // Kick off the 30-day (month) refresh, NON-BLOCKING: fire-and-forget with a
    // swallowing catch so a refresh failure never blocks landing on the
    // dashboard. Endpoint verified from the codebase — the refresh router is
    // mounted under /api/refresh (ChartsPage.tsx uses the same path), NOT
    // /api/tanks as the plan's shorthand suggested.
    if (selectedTank) {
      api
        .post(`/api/refresh/tanks/${selectedTank.id}/refresh`, { time_period: 'month' })
        .catch(() => {});
    }
    // Record that a device was connected so FinishStep can tailor its copy.
    setField({ connectedDevice: true });
    // Funnel: first-value completed via the device-connect variant.
    trackOnboarding('step_completed', { step: 'first-value', variant: 'device' });
    next();
  };

  const handleTestSuccess = () => {
    // A logged water test does not connect a device; just advance.
    // Funnel: first-value completed via the manual-test variant.
    trackOnboarding('step_completed', { step: 'first-value', variant: 'test' });
    next();
  };

  const skip = () => {
    // Funnel: the user skipped the first-value action.
    trackOnboarding('step_skipped', { step: 'first-value' });
    next();
  };

  return (
    <Stack gap="lg" data-testid="onboarding-step-first-value">
      {isDeviceBranch ? (
        <>
          <Stack gap="xs">
            <Title order={1}>Connect your first device</Title>
            <Text c="dimmed">
              Enter your credentials to connect. We'll pull in your recent history so your
              dashboard has data to show right away.
            </Text>
          </Stack>

          {selectedTank && (
            <AddDeviceForm
              tankId={selectedTank.id}
              initialDeviceType={firstBrand}
              submitLabel="Connect"
              onSuccess={handleDeviceSuccess}
            />
          )}
        </>
      ) : (
        <>
          <Stack gap="xs">
            <Title order={1}>Log your first water test</Title>
            <Text c="dimmed">
              Enter a single reading — it takes about 30 seconds — and watch it chart on your
              dashboard.
            </Text>
          </Stack>

          {selectedTank && <WaterTestForm onSuccess={handleTestSuccess} />}
        </>
      )}

      <Anchor
        component="button"
        type="button"
        c="dimmed"
        size="sm"
        onClick={skip}
        ta="center"
      >
        Skip for now
      </Anchor>
    </Stack>
  );
}
